import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from '@framework/i18n';
import { Button, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import React, { useMemo } from 'react';
import { useFormState } from 'react-hook-form';
import { useFormAugmentedContext } from '../augmented/FormAugmentedContext';

export interface ISubmitButtonProps extends ButtonProps {
  /**
   * Messaggio di default da mostrare quando il form è pristine, ovvero
   * se non ci sono modifiche da salvare. Se `null`, il tooltip non viene
   * mostrato.
   */
  pristineMessage?: string | null;
  hideSaveIcon?: boolean;
  /**
   * Se posizionato fuori dal form, il submit non può essere eseuito
   * con `htmlType="submit"`, ma deve essere eseguito manualmente
   */
  isOutsideForm?: boolean;
}

export function SubmitButton(props: ISubmitButtonProps) {
  const {
    pristineMessage,
    hideSaveIcon,
    isOutsideForm,
    children,
    ...otherProps
  } = props;
  const { t } = useTranslation();

  // Destructuring obbligatorio per il corretto funzionamento di useFormState
  const { isDirty, isSubmitting } = useFormState();
  const { submitForm } = useFormAugmentedContext();

  const { htmlType, onClick } = useMemo(() => {
    if (!isOutsideForm) {
      return {
        htmlType: 'submit' as const,
        onClick: undefined,
      };
    }

    return {
      htmlType: 'button' as const,
      onClick: (e: React.BaseSyntheticEvent) => {
        submitForm.current?.(e).catch((err: any) => {
          console.error(err);
        });
      },
    };
  }, [isOutsideForm, submitForm]);

  const isDisabled = props.disabled ?? !isDirty;

  const tooltipTitle = useMemo(() => {
    if (!isDisabled) return undefined;
    // Se il messaggio è `null`, non mostriamo il tooltip come descritto nelle props
    if (pristineMessage === null) return undefined;

    return (
      pristineMessage ??
      t(
        'form.submit-button.tooltip-not-dirty',
        'Non ci sono modifiche da salvare',
      )
    );
  }, [isDisabled, pristineMessage, t]);

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        icon={hideSaveIcon ? undefined : <SaveOutlined aria-hidden />}
        type="primary"
        htmlType={htmlType}
        onClick={onClick}
        size="large"
        {...otherProps}
        loading={isSubmitting}
        disabled={isDisabled}
      >
        {children ?? t('form.submit-button.save', 'Salva')}
      </Button>
    </Tooltip>
  );
}
